import { useAppSelector } from 'hooks/redux';
import { FC, useEffect, useRef } from 'react';
import { useStarkNetSnap } from 'services';
import { Transaction, TransferOffer } from 'types';
import {
  defaultAccount,
  TRANSACTIONS_REFRESH_FREQUENCY,
  OFFERS_REFRESH_FREQUENCY,
} from 'utils/constants';
import { IListProps } from '../List/List.view';
import {
  TransactionListItem,
  TransferOfferListItem,
} from './TransactionListItem';
import { Wrapper } from './TransactionsList.style';
import { Divider } from '@mui/material';

interface Props {
  transactions: Transaction[];
}

export const TransactionsListView = ({ transactions }: Props) => {
  const { getTransactions, getOffers } = useStarkNetSnap();
  const networks = useAppSelector((state) => state.networks);
  const currentAccount = useAppSelector((state) => state.wallet.currentAccount);
  const erc20TokenBalanceSelected = useAppSelector(
    (state) => state.wallet.erc20TokenBalanceSelected,
  );
  const walletTransactions = useAppSelector(
    (state) => state.wallet.transactions,
  );

  // Use transferOffers from Redux state
  const transferOffers = useAppSelector((state) => state.wallet.transferOffers);

  const timeoutHandle = useRef(setTimeout(() => {}));
  const chainId = networks.items[networks.activeNetwork]?.chainId;

  useEffect(() => {
    if (chainId && erc20TokenBalanceSelected.instrumentAdmin) {
      clearTimeout(timeoutHandle.current);
      timeoutHandle.current = setTimeout(
        () =>
          getTransactions(
            currentAccount.address,
            erc20TokenBalanceSelected.instrumentAdmin,
            10,
            chainId,
            false,
            true,
          ),
        TRANSACTIONS_REFRESH_FREQUENCY,
      );
      return () => clearTimeout(timeoutHandle.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletTransactions]);

  useEffect(
    () => {
      if (
        chainId &&
        erc20TokenBalanceSelected.instrumentAdmin &&
        currentAccount.address !== defaultAccount.address
      ) {
        clearTimeout(timeoutHandle.current);
        getTransactions(
          currentAccount.address,
          erc20TokenBalanceSelected.instrumentAdmin,
          10,
          chainId,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      erc20TokenBalanceSelected.instrumentAdmin,
      erc20TokenBalanceSelected.chainId,
      currentAccount.address,
      currentAccount.chainId,
      chainId,
    ],
  );

  useEffect(() => {
    if (!chainId || currentAccount.address === defaultAccount.address) {
      // No need to clear transferOffers, handled by Redux
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const loadOffers = async () => {
      try {
        await getOffers(currentAccount.address, chainId);
      } catch (e) {
        // Errors handled in getOffers
      }

      if (!cancelled) {
        timeoutId = setTimeout(loadOffers, OFFERS_REFRESH_FREQUENCY);
      }
    };

    loadOffers();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, currentAccount.address]);

  const transactionsToRender =
    transactions.length > 0 ? transactions : walletTransactions;

  return (
    <>
      {transferOffers && transferOffers.length > 0 && (
        <Wrapper<FC<IListProps<TransferOffer>>>
          data={transferOffers}
          render={(offer) => <TransferOfferListItem offer={offer} />}
          keyExtractor={(offer) => offer.contractId}
          style={{ overflow: 'hidden scroll', maxHeight: 160, minHeight: 160 }}
        />
      )}

      <Divider />

      <Wrapper<FC<IListProps<Transaction>>>
        data={transactionsToRender}
        render={(transaction) => (
          <TransactionListItem transaction={transaction} />
        )}
        keyExtractor={(transaction) => transaction.updateId}
      />
    </>
  );
};
